import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type * as API from "@shared-core/data/api-types";
import type { UserListItem } from "@shared-core/data/adapter";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../app/components/primitives/table";
import { Button } from "../app/components/primitives/button";
import { Input } from "../app/components/primitives/input";
import { Badge } from "../app/components/primitives/badge";
import {
  EntityCombobox,
  type EntityOption,
} from "../app/components/patterns/EntityCombobox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../app/components/primitives/alert-dialog";
import { Plus, Pencil, Archive, Check, X, Users } from "lucide-react";

// A composition of two existing patterns — SettingsTicketCategoriesPage
// (curated list CRUD, inline table editing) and SettingsBuddiesPage
// (membership picker via chips) — introducing no new pattern, per the
// frontend update spec §4.
//
// Site is a free-text field, not a fixed dropdown: sites are derived from
// department rows, and hardcoding a site list here would reintroduce the
// deploy requirement the backend deliberately avoids (spec 03 INV-9).
// Existing sites are offered as <datalist> suggestions; typing a new one is
// always allowed.

const CENTRAL_GROUP_KEY = "__central__";

function groupBySite(
  departments: API.Department[],
): { site: string | null; departments: API.Department[] }[] {
  const groups = new Map<string, API.Department[]>();
  for (const d of departments) {
    const key = d.site ?? CENTRAL_GROUP_KEY;
    const bucket = groups.get(key) ?? [];
    bucket.push(d);
    groups.set(key, bucket);
  }
  const siteNames = Array.from(groups.keys())
    .filter((k) => k !== CENTRAL_GROUP_KEY)
    .sort((a, b) => a.localeCompare(b));
  const orderedKeys = groups.has(CENTRAL_GROUP_KEY)
    ? [CENTRAL_GROUP_KEY, ...siteNames]
    : siteNames;
  return orderedKeys.map((key) => ({
    site: key === CENTRAL_GROUP_KEY ? null : key,
    departments: [...groups.get(key)!].sort((a, b) =>
      a.name.localeCompare(b.name),
    ),
  }));
}

export function SettingsDepartmentsPage() {
  const dataAdapter = useDataAdapter();
  const [departments, setDepartments] = useState<API.Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [showRetired, setShowRetired] = useState(false);

  const [isCreating, setIsCreating] = useState(false);
  const [newSlug, setNewSlug] = useState("");
  const [newName, setNewName] = useState("");
  const [newSite, setNewSite] = useState("");
  const [newLead, setNewLead] = useState<EntityOption | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [editingSite, setEditingSite] = useState("");
  const [editingLead, setEditingLead] = useState<EntityOption | null>(null);

  // The currently open panel's department, and the only thing that says a
  // panel is open. Holds full detail (incl. `members` with names), which the
  // list-read `departments` entry omits.
  const [membersPanelDepartment, setMembersPanelDepartment] =
    useState<API.Department | null>(null);
  const [membersPanelSelection, setMembersPanelSelection] = useState<string[]>(
    [],
  );
  const [membersPanelLoading, setMembersPanelLoading] = useState(false);

  const [retireId, setRetireId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const departmentList = await dataAdapter.listDepartments(!showRetired);
      setDepartments(departmentList);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRetired]);

  const knownSites = useMemo(
    () =>
      Array.from(
        new Set(
          departments.map((d) => d.site).filter((s): s is string => Boolean(s)),
        ),
      ).sort((a, b) => a.localeCompare(b)),
    [departments],
  );
  const grouped = useMemo(() => groupBySite(departments), [departments]);

  // Shared fetchOptions for both lead pickers — server-side search against
  // the same endpoint the members panel uses, so past-200 rosters stay
  // reachable (see `load` above). Matches TicketModal's fetchAssignees.
  const fetchUserOptions = useCallback(
    async (q: string): Promise<EntityOption[]> => {
      const resp = await dataAdapter.listUsers({
        q: q || undefined,
        page: 1,
        pageSize: 25,
      });
      return resp.items.map((u) => ({
        id: u.id,
        label: u.name,
        hint: u.email,
      }));
    },
    [dataAdapter],
  );

  const handleCreate = async () => {
    const slug = newSlug.trim();
    const name = newName.trim();
    if (!slug || !name || !newLead) return;
    setIsSaving(true);
    try {
      await dataAdapter.createDepartment({
        slug,
        name,
        lead_user_id: newLead.id,
        site: newSite.trim() || null,
      });
      setIsCreating(false);
      setNewSlug("");
      setNewName("");
      setNewSite("");
      setNewLead(null);
      await load();
    } finally {
      setIsSaving(false);
    }
  };

  const handleStartEdit = (department: API.Department) => {
    setEditingId(department.id);
    setEditingName(department.name);
    setEditingSite(department.site ?? "");
    setEditingLead(
      department.lead_user_id
        ? { id: department.lead_user_id, label: department.lead_name }
        : null,
    );
  };

  const handleSaveEdit = async () => {
    if (!editingId) return;
    const name = editingName.trim();
    if (!name || !editingLead) return;
    setIsSaving(true);
    try {
      await dataAdapter.updateDepartment(editingId, {
        name,
        lead_user_id: editingLead.id,
        site: editingSite.trim() || null,
      });
      setEditingId(null);
      await load();
    } finally {
      setIsSaving(false);
    }
  };

  const handleRetire = async () => {
    if (!retireId) return;
    await dataAdapter.retireDepartment(retireId);
    setRetireId(null);
    await load();
  };

  const handleOpenMembersPanel = async (department: API.Department) => {
    // Shown immediately for the panel's title/lead badge; replaced below
    // once the detail fetch resolves `members` with names.
    setMembersPanelDepartment(department);
    setMembersPanelLoading(true);
    try {
      // The list read omits members (avoids an N+1 fetch) — fetch full
      // detail on demand, only when the admin actually opens this
      // department's member editor.
      const detail = await dataAdapter.getDepartmentDetail(department.id);
      setMembersPanelDepartment(detail);
      setMembersPanelSelection(detail.members?.map((m) => m.user_id) ?? []);
    } finally {
      setMembersPanelLoading(false);
    }
  };

  const handleToggleMember = (userId: string) => {
    setMembersPanelSelection((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
  };

  const handleSaveMembers = async () => {
    if (!membersPanelDepartment) return;
    setIsSaving(true);
    try {
      await dataAdapter.setDepartmentMembers(
        membersPanelDepartment.id,
        membersPanelSelection,
      );
      setMembersPanelDepartment(null);
      await load();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl mb-2">Departments</h1>
          <p className="text-sm text-muted-foreground">
            The fallback routing target for tickets that can't reach an
            individual. A department is a default view and notification target
            for its members, never an access boundary — retiring one keeps it on
            tickets it already owns and just stops routing new ones there.
          </p>
        </div>
        <Button onClick={() => setIsCreating(true)}>
          <Plus className="size-4 mr-2" />
          New Department
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={() => setShowRetired(!showRetired)}
          className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
            showRetired
              ? "bg-muted text-foreground font-medium"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {showRetired ? "Showing retired" : "Show retired"}
        </button>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Lead</TableHead>
              <TableHead>Members</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-32">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isCreating && (
              <TableRow>
                <TableCell>
                  <div className="space-y-1.5">
                    <Input
                      autoFocus
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      placeholder="Name, e.g. Prepress - Exeter"
                    />
                    <Input
                      value={newSlug}
                      onChange={(e) => setNewSlug(e.target.value)}
                      placeholder="Slug, e.g. prepress-exeter (immutable once created)"
                    />
                    <Input
                      list="department-site-suggestions"
                      value={newSite}
                      onChange={(e) => setNewSite(e.target.value)}
                      placeholder="Site (leave blank for central/company-wide)"
                    />
                  </div>
                </TableCell>
                <TableCell colSpan={4}>
                  <div className="flex items-start gap-2">
                    <EntityCombobox
                      value={newLead}
                      onChange={setNewLead}
                      fetchOptions={fetchUserOptions}
                      placeholder="Choose a lead"
                      ariaLabel="Lead"
                      clearable={false}
                      className="w-[200px]"
                    />
                    <Button
                      size="sm"
                      onClick={handleCreate}
                      disabled={
                        !newSlug.trim() ||
                        !newName.trim() ||
                        !newLead ||
                        isSaving
                      }
                    >
                      Add
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setIsCreating(false);
                        setNewSlug("");
                        setNewName("");
                        setNewSite("");
                        setNewLead(null);
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            )}
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="text-center py-8 text-muted-foreground"
                >
                  Loading...
                </TableCell>
              </TableRow>
            ) : loadError ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="text-center py-8 text-destructive"
                >
                  Couldn't load departments. Try refreshing the page.
                </TableCell>
              </TableRow>
            ) : departments.length === 0 && !isCreating ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="text-center py-8 text-muted-foreground"
                >
                  No departments yet
                </TableCell>
              </TableRow>
            ) : (
              grouped.map((group) => (
                <GroupRows
                  key={group.site ?? CENTRAL_GROUP_KEY}
                  site={group.site}
                  departments={group.departments}
                  editingId={editingId}
                  editingName={editingName}
                  editingSite={editingSite}
                  editingLead={editingLead}
                  fetchUserOptions={fetchUserOptions}
                  isSaving={isSaving}
                  onStartEdit={handleStartEdit}
                  onChangeEditingName={setEditingName}
                  onChangeEditingSite={setEditingSite}
                  onChangeEditingLead={setEditingLead}
                  onSaveEdit={handleSaveEdit}
                  onCancelEdit={() => setEditingId(null)}
                  onOpenMembersPanel={handleOpenMembersPanel}
                  onRequestRetire={setRetireId}
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <datalist id="department-site-suggestions">
        {knownSites.map((site) => (
          <option key={site} value={site} />
        ))}
      </datalist>

      {membersPanelDepartment && (
        <MembersPanel
          department={membersPanelDepartment}
          selection={membersPanelSelection}
          loading={membersPanelLoading}
          isSaving={isSaving}
          onToggleMember={handleToggleMember}
          onSave={handleSaveMembers}
          onCancel={() => setMembersPanelDepartment(null)}
        />
      )}

      <AlertDialog open={!!retireId} onOpenChange={() => setRetireId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Retire this department?</AlertDialogTitle>
            <AlertDialogDescription>
              Existing tickets keep this department. It stops receiving new ones
              and is removed from the routing target list — there's no way to
              reactivate it, other than setting a lead again if it never had
              one.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRetire}>Retire</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function GroupRows({
  site,
  departments,
  editingId,
  editingName,
  editingSite,
  editingLead,
  fetchUserOptions,
  isSaving,
  onStartEdit,
  onChangeEditingName,
  onChangeEditingSite,
  onChangeEditingLead,
  onSaveEdit,
  onCancelEdit,
  onOpenMembersPanel,
  onRequestRetire,
}: {
  site: string | null;
  departments: API.Department[];
  editingId: string | null;
  editingName: string;
  editingSite: string;
  editingLead: EntityOption | null;
  fetchUserOptions: (query: string) => Promise<EntityOption[]>;
  isSaving: boolean;
  onStartEdit: (department: API.Department) => void;
  onChangeEditingName: (value: string) => void;
  onChangeEditingSite: (value: string) => void;
  onChangeEditingLead: (value: EntityOption | null) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onOpenMembersPanel: (department: API.Department) => void;
  onRequestRetire: (departmentId: string) => void;
}) {
  return (
    <>
      <TableRow className="bg-muted/30 hover:bg-muted/30">
        <TableCell
          colSpan={5}
          className="text-xs font-medium text-muted-foreground uppercase py-1.5"
        >
          {site ?? "Central"}
        </TableCell>
      </TableRow>
      {departments.map((department) => {
        const isEditing = editingId === department.id;
        return (
          <TableRow key={department.id}>
            <TableCell className="font-medium align-top">
              {isEditing ? (
                <div className="space-y-1.5">
                  <Input
                    autoFocus
                    value={editingName}
                    onChange={(e) => onChangeEditingName(e.target.value)}
                  />
                  <Input
                    list="department-site-suggestions"
                    value={editingSite}
                    onChange={(e) => onChangeEditingSite(e.target.value)}
                    placeholder="Site (leave blank for central/company-wide)"
                  />
                </div>
              ) : (
                <div>
                  <div>{department.name}</div>
                  {/* Slug is displayed but never editable — it is the
                      routing key (artifact 1, INV-1). */}
                  <div className="text-xs text-muted-foreground font-mono">
                    {department.slug}
                  </div>
                </div>
              )}
            </TableCell>
            <TableCell className="align-top">
              {isEditing ? (
                <EntityCombobox
                  value={editingLead}
                  onChange={onChangeEditingLead}
                  fetchOptions={fetchUserOptions}
                  placeholder="Choose a lead"
                  ariaLabel="Lead"
                  clearable={false}
                  className="w-[180px]"
                />
              ) : department.lead_user_id ? (
                department.lead_name
              ) : (
                <span className="text-muted-foreground italic">
                  No lead — inactive until one is set
                </span>
              )}
            </TableCell>
            <TableCell className="align-top">
              <Button
                variant="ghost"
                size="sm"
                className="gap-1.5"
                disabled={!department.is_active}
                onClick={() => onOpenMembersPanel(department)}
              >
                <Users className="size-3.5" />
                {department.member_count} member
                {department.member_count === 1 ? "" : "s"}
              </Button>
            </TableCell>
            <TableCell className="align-top">
              <Badge variant={department.is_active ? "default" : "secondary"}>
                {department.is_active ? "Active" : "Retired"}
              </Badge>
            </TableCell>
            <TableCell className="align-top">
              {isEditing ? (
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label="Save changes"
                    onClick={onSaveEdit}
                    disabled={!editingName.trim() || !editingLead || isSaving}
                  >
                    <Check className="size-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label="Cancel editing"
                    onClick={onCancelEdit}
                  >
                    <X className="size-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex gap-1">
                  {/* Edit stays available on an inactive department —
                      deliberately not gated on is_active. A seeded
                      department starts inactive with no lead (INV-2) and
                      the only way an admin brings it online is setting one
                      here (INV-5 reactivates it); hiding Edit for inactive
                      rows would make that impossible through this page.
                      Editing a deliberately-retired department (one that
                      already has a lead) is harmless — INV-5 only
                      reactivates when there was no lead to begin with. */}
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Edit ${department.name}`}
                    onClick={() => onStartEdit(department)}
                  >
                    <Pencil className="size-4" />
                  </Button>
                  {/* Retire is hidden once already retired — idempotent on
                      the backend, but a no-op action has nothing to offer
                      here. */}
                  {department.is_active && (
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Retire ${department.name}`}
                      onClick={() => onRequestRetire(department.id)}
                    >
                      <Archive className="size-4 text-destructive" />
                    </Button>
                  )}
                </div>
              )}
            </TableCell>
          </TableRow>
        );
      })}
    </>
  );
}

const MEMBER_SEARCH_DEBOUNCE_MS = 200;
const MEMBER_SEARCH_PAGE_SIZE = 50;

function MembersPanel({
  department,
  selection,
  loading,
  isSaving,
  onToggleMember,
  onSave,
  onCancel,
}: {
  department: API.Department;
  selection: string[];
  loading: boolean;
  isSaving: boolean;
  onToggleMember: (userId: string) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  const dataAdapter = useDataAdapter();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<UserListItem[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  // The response already resolves current members to names — seed the
  // cache from that so chips render immediately without a fetch.
  const initialNames = useMemo(
    () => new Map((department.members ?? []).map((m) => [m.user_id, m.name])),
    [department.members],
  );
  // Names learned from search results, for a newly picked member not in
  // `initialNames` (someone just added who wasn't already a member).
  // Merged with initialNames when rendering chips, never overwriting it.
  const [extraNames, setExtraNames] = useState<Map<string, string>>(new Map());
  const fetchSeq = useRef(0);
  const isFirstSearch = useRef(true);

  const runSearch = useCallback(
    async (q: string) => {
      const seq = ++fetchSeq.current;
      setSearching(true);
      setSearchError(null);
      try {
        const page = await dataAdapter.listUsers({
          q: q || undefined,
          page: 1,
          pageSize: MEMBER_SEARCH_PAGE_SIZE,
        });
        if (seq === fetchSeq.current) {
          setResults(page.items);
          setExtraNames((prev) => {
            const next = new Map(prev);
            for (const u of page.items) next.set(u.id, u.name);
            return next;
          });
        }
      } catch {
        if (seq === fetchSeq.current) {
          setSearchError("Couldn't search people. Try again.");
        }
      } finally {
        if (seq === fetchSeq.current) {
          setSearching(false);
        }
      }
    },
    [dataAdapter],
  );

  // First load runs immediately; every later keystroke is debounced. One
  // effect rather than a mount effect plus a debounced one, which would
  // fire two identical empty-query searches on open.
  useEffect(() => {
    if (isFirstSearch.current) {
      isFirstSearch.current = false;
      void runSearch("");
      return;
    }
    const handle = setTimeout(() => {
      void runSearch(query.trim());
    }, MEMBER_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const nameFor = useCallback(
    (userId: string) =>
      initialNames.get(userId) ?? extraNames.get(userId) ?? userId,
    [initialNames, extraNames],
  );

  const trimmedQuery = query.trim();

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={onCancel}
    >
      <div
        className="bg-background border border-border rounded-lg shadow-lg w-full max-w-md mx-4 max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-border">
          <h2 className="text-lg">Members — {department.name}</h2>
        </div>
        {loading ? (
          <div className="p-6">
            <p className="text-sm text-muted-foreground">Loading...</p>
          </div>
        ) : (
          <>
            <div className="px-6 pt-4 space-y-3">
              <Input
                autoFocus
                aria-label="Search people by name or email"
                placeholder="Search people by name or email"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              {selection.length > 0 && (
                <div data-testid="member-selected-chips">
                  <p className="text-xs text-muted-foreground mb-1.5">
                    {selection.length} member
                    {selection.length === 1 ? "" : "s"}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {selection.map((userId) => (
                      <Badge
                        key={userId}
                        variant="outline"
                        className="gap-1 pr-1"
                      >
                        {nameFor(userId)}
                        <button
                          type="button"
                          aria-label={`Remove ${nameFor(userId)}`}
                          onClick={() => onToggleMember(userId)}
                          className="hover:text-destructive"
                        >
                          <X className="size-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div
              className="p-6 pt-3 space-y-1 overflow-y-auto"
              data-testid="member-search-results"
            >
              {searching && (
                <p className="text-xs text-muted-foreground px-2 pb-1">
                  Searching…
                </p>
              )}
              {searchError && (
                <p className="text-xs text-destructive px-2 pb-1">
                  {searchError}
                </p>
              )}
              {!searching &&
                !searchError &&
                results !== null &&
                results.length === 0 &&
                (trimmedQuery ? (
                  <p className="text-sm text-muted-foreground px-2">
                    No people match "{trimmedQuery}"
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground px-2">
                    No people found
                  </p>
                ))}
              {(results ?? []).map((u) => (
                <label
                  key={u.id}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-md text-sm cursor-pointer hover:bg-accent/50"
                >
                  <input
                    type="checkbox"
                    checked={selection.includes(u.id)}
                    onChange={() => onToggleMember(u.id)}
                    className="accent-primary"
                  />
                  <div className="flex flex-col min-w-0">
                    <span className="truncate">{u.name}</span>
                    <span className="text-xs text-muted-foreground truncate">
                      {u.email}
                    </span>
                  </div>
                  {u.id === department.lead_user_id && (
                    <Badge variant="outline" className="ml-auto shrink-0">
                      Lead
                    </Badge>
                  )}
                </label>
              ))}
            </div>
          </>
        )}
        <div className="px-6 py-4 border-t border-border flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={loading || isSaving}>
            {isSaving ? "Saving…" : "Save members"}
          </Button>
        </div>
      </div>
    </div>
  );
}
